import React, { useState, useContext, useEffect } from 'react'
import {
  Input,
  Button,
  Form,
  Table,
  Select,
  message,
  DatePicker,
  Space,
} from 'antd'
import Papa from 'papaparse'
import { useSetState } from 'react-use'
import { sleep } from '@guillotinaweb/react-gmi'
import { ErpClientContext } from '../services/erp'
const { RangePicker } = DatePicker

// Allowed extensions for input file
const allowedExtensions = [
  'csv',
  'vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

const { Option } = Select

const initial = {
  id_pedido_column: '',
  amount_order_column: '',
  id_comp: '',
  codigo_factura: 0,
  fecha_factura: '',
  comision_fija: 0,
}

function orderListByKey(data, key, order) {
  const compareValues = (key, order = 'asc') => (elemA, elemB) => {
    if (!elemA.hasOwnProperty(key) || !elemB.hasOwnProperty(key)) return 0
    const comparison = elemA[key].localeCompare(elemB[key])
    return order === 'desc' ? comparison * -1 : comparison
  }
  return data.sort(compareValues(key, order))
}

function formatCurrency(value) {
  return Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(value)
}

function getCountryCode(invoice) {
  return String(invoice.iso_code || invoice.id_pais || '').toUpperCase()
}

export default function ImportInvoices({ props }) {
  const erpService = useContext(ErpClientContext)
  const [state, setState] = useSetState(initial)
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState([])
  const [columnsTable, setColumnsTable] = useState(null)
  const [optionsColumns, setOptionsColumns] = useState([])
  const [optionConfirmData, setOptionConfirmData] = useState(false)
  const [dataTransportCost, setDataTransportCost] = useState(false)
  const [disableInputFile, setDisableInputFile] = useState(false)
  const [filterData, setFilterData] = useState({
    template: '',
    fecha_desde: '',
    fecha_hasta: '',
  })
  const [optionFilter, setOptionFilter] = useState(true)
  const [totalFilter, setTotalFilter] = useState('')
  const companies = props.companies
  const template = props.template
  const title = props.title
  const labelTemplate = props.labelTemplate

  useEffect(() => {
    init()
  }, [])
  async function init() {
    const res = await erpService.getImportedInvoice(template)

    const filterColumn = () =>
      companies.map((val) => ({
        text: val.label,
        value: val.label,
      }))

    const columnsAlignRight = [
      'total_value_of_matched_costs',
      'total_value_of_not_matched_costs',
    ]

    setColumnsTable(
      Object.keys(res[0]).map((column) => ({
        title: column.replace(/_/g, ' ').toUpperCase(),
        dataIndex: column,
        columnKey: column,
        sorter:
          column === 'invoice_date'
            ? (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date)
            : null,
        filters: column === 'id_company' ? filterColumn() : null,
        onFilter: (value, record) =>
          record.id_pasarela_company.startsWith(value),
        filterSearch: true,
        align: columnsAlignRight.includes(column) ? 'right' : null,
      }))
    )
    const dataColumnsImportedInvoices = res.map((dataRes) => {
      return {
        invoice_date: dataRes.invoice_date,
        invoice_number: dataRes.invoice_number,
        name_company: dataRes.name_company,
        total_value_of_matched_costs: formatCurrency(
          dataRes.total_value_of_matched_costs
        ),
        total_value_of_not_matched_costs: formatCurrency(
          dataRes.total_value_of_not_matched_costs
        ),
      }
    })

    setData(dataColumnsImportedInvoices)
  }

  async function transportCost() {
    if (
      state.id_pedido_column === '' ||
      state.amount_order_column === '' ||
      state.id_company === '' ||
      state.codigo_factura === 0 ||
      state.fecha_factura === ''
    ) {
      message.error('All fields are required')
      return
    }

    setLoading(true)

    const res = await erpService.fetch('/orders/@invoice-cost', {
      method: 'POST',
      body: JSON.stringify({
        id_company: state.id_company,
        codigo_factura: state.codigo_factura,
        fecha_factura: state.fecha_factura,
        comision_fija: state.comision_fija,
        data: data.map((values) => ({
          cod_order: values[state.id_pedido_column],
          cost: values[state.amount_order_column],
        })),
      }),
    })

    const response = await res.json()

    if (res.status === 200) {
      const resData = response.lines_file.map((responseValue) => ({
        line: responseValue.line,
        cod_order: responseValue.cod_order,
        cost: formatCurrency(responseValue.cost),
      }))
      setDataTransportCost(response)
      setData(resData)
      setColumnsTable(
        Object.keys(response.lines_file[0]).map((column) => ({
          title: column,
          dataIndex: column,
          key: column,
        }))
      )
      setOptionConfirmData(false)
    } else {
      message.error(`Oops! ${JSON.stringify(response)}`)
    }

    setLoading(false)
  }

  async function transportCostConfirm() {
    setLoading(true)

    dataTransportCost.template = template

    const resConfirm = await erpService.fetch('/orders/@invoice-cost-confirm', {
      method: 'POST',
      body: JSON.stringify(dataTransportCost),
    })

    if (resConfirm.status === 200) {
      message.success('Datos registrados correctamente!')
      await sleep(2000)
      window.location.reload(false)
    } else {
      const messageError = await resConfirm.json()
      message.error(`Oops! ${JSON.stringify(messageError)}`)
    }

    setLoading(false)
  }

  async function handleFileChange(e) {
    if (e.target.files.length) {
      const inputFile = e.target.files[0]
      const fileExtension = inputFile?.type.split('/')[1]
      if (!allowedExtensions.includes(fileExtension)) {
        message.error('Please input a csv or xlsx file')
        await sleep(2000)
        window.location.reload(false)
      }
      uploadFile(inputFile)
    }
  }
  async function uploadFile(file) {
    const formData = new FormData()
    formData.append('file', file)

    var typeFile = 'xlxs'
    if (file.type === 'text/csv') {
      typeFile = 'csv'
    }
    const res = await erpService.fetch(`/orders/@upload_invoice/${typeFile}`, {
      method: 'POST',
      body: formData,
    })

    const response = await res.json()

    setColumnsTable(
      Object.keys(response['data'][0]).map((column) => ({
        title: column,
        dataIndex: column,
        key: column,
        width: '120px',
      }))
    )

    setOptionsColumns(
      Object.keys(response['data'][0]).map((column) => ({
        value: column,
        label: column,
      }))
    )

    setData(response['data'])
    setOptionConfirmData(true)
    setDisableInputFile(true)
    setOptionFilter(false)
  }

  async function filterDataInvoices() {
    setLoading(true)

    try {
      const resFilterDate = await erpService.fetch(
        '/orders/@grouping_invoices_country',
        {
          method: 'POST',
          body: JSON.stringify(filterData),
        }
      )

      const response = await resFilterDate.json()

      if (response.data_filtered) {
        const dataFilterDate = response.data_filtered.map((dateResponse) => {
          const id_company = (id_companyia) => {
            for (const element of companies) {
              if (Object.values(element).includes(id_companyia)) {
                return element.label
              }
            }
            return id_companyia
          }
          const idCompany = id_company(dateResponse.id_company)
          return {
            id_pais: getCountryCode(dateResponse),
            id_company: idCompany,
            codigo_factura: dateResponse.codigo_factura,
            fecha_factura: dateResponse.fecha_factura,
            total: formatCurrency(dateResponse.total),
          }
        })

        if (!dataFilterDate.length) {
          setData([])
          setTotalFilter(formatCurrency(response.total_amount || 0))
          message.info('No results found')
          return
        }

        const filterColumn = (value) => {
          const columnsFilter = ['id_pais', 'id_company']
          const labelCompany = {}
          if (columnsFilter.includes(value)) {
            const repetidos = []
            response.data_filtered.forEach((element) => {
              const filterValue =
                value === 'id_pais' ? getCountryCode(element) : element[value]
              if (!repetidos.includes(filterValue)) {
                repetidos.push(filterValue)
                if (value === 'id_company') {
                  companies.forEach((company) => {
                    if (company['value'] === filterValue) {
                      labelCompany[filterValue] = company['label']
                    }
                  })
                }
              }
            })
            return repetidos.map((val) => ({
              text: labelCompany[val] ? labelCompany[val] : val,
              value: labelCompany[val] ? labelCompany[val] : val,
            }))
          }
        }

        setColumnsTable(
          Object.keys(dataFilterDate[0]).map((column) => ({
            title: column.replace(/_/g, ' ').toUpperCase(),
            dataIndex: column,
            columnKey: column,
            sorter:
              column === 'invoice_date'
                ? (a, b) => new Date(a.invoice_date) - new Date(b.invoice_date)
                : (a, b) => {
                    if (a[column] < b[column]) return -1
                    if (a[column] > b[column]) return 1
                    return 0
                  },
            filters: filterColumn(column),
            onFilter:
              column === 'id_pais'
                ? (value, record) =>
                    record.id_pais.toLowerCase().startsWith(value.toLowerCase())
                : (value, record) => record.id_company.startsWith(value),
            filterSearch: true,
          }))
        )

        setOptionsColumns(
          Object.keys(dataFilterDate[0]).map((column) => ({
            value: column,
            label: column,
          }))
        )

        setData(dataFilterDate)
        setTotalFilter(formatCurrency(response.total_amount))
      } else {
        message.error('Incorrect dates')
      }
    } catch (error) {
      message.error(`Oops! ${error.message}`)
    } finally {
      setLoading(false)
    }
  }
  return (
    <div
      className="container"
      style={{
        backgroundColor: '#fff',
        padding: '30px',
      }}
    >
      <div className="level-left">
        <h2 className="title is-size-5">{title}</h2>
      </div>
      <Form onFinish={optionConfirmData ? transportCost : transportCostConfirm}>
        {!disableInputFile && (
          <div>
            <label htmlFor="csvInput" style={{ display: 'block' }}>
              Import File
            </label>
            <Input
              onChange={handleFileChange}
              id="csvInput"
              name="file"
              type="File"
            />
          </div>
        )}
        {optionConfirmData && (
          <div style={{ 'margin-top': '10px' }}>
            <div className="field">
              <label className="label">
                Select the column corresponding to the order code:
              </label>
              <Select
                showSearch
                defaultValue="Select column of order id"
                key="id_pedido_column"
                onChange={(value) => {
                  setState({
                    ...state,
                    id_pedido_column: value,
                  })
                }}
                style={{
                  width: 250,
                }}
              >
                {orderListByKey(optionsColumns, 'label').map((optionCol) => (
                  <Option value={optionCol.value}>{optionCol.label}</Option>
                ))}
              </Select>
            </div>
            <div className="field">
              <label className="label">Select column of valor the order:</label>
              <Select
                showSearch
                defaultValue="Select column of valor the order"
                key="column_amount_order"
                onChange={(value) => {
                  setState({
                    ...state,
                    amount_order_column: value,
                  })
                }}
                style={{
                  width: 250,
                }}
              >
                {orderListByKey(optionsColumns, 'label').map((optionCol) => (
                  <Option value={optionCol.value}>{optionCol.label}</Option>
                ))}
              </Select>
            </div>
            <div className="field">
              <label className="label">{labelTemplate}:</label>
              <Select
                defaultValue={labelTemplate}
                key="id_company"
                onChange={(value) => {
                  setState({
                    ...state,
                    id_company: value,
                  })
                }}
                style={{
                  width: 250,
                }}
              >
                {companies.map((company) => (
                  <Option value={company.value}>{company.label}</Option>
                ))}
              </Select>
            </div>
            {state.id_company === 'pyco' ? (
              <div className="field">
                <label className="label">Fixed commission:</label>
                <Input
                  className="is-size-6"
                  id="comision_fija"
                  onChange={(e) => {
                    setState({
                      ...state,
                      comision_fija: e.target.value,
                    })
                  }}
                  style={{
                    width: 250,
                  }}
                />
              </div>
            ) : null}
            <div className="field">
              <label className="label">Enter invoice id:</label>
              <Input
                className="is-size-6"
                id="codigo_factura"
                onChange={(e) => {
                  setState({
                    ...state,
                    codigo_factura: e.target.value,
                  })
                }}
                style={{
                  width: 250,
                }}
              />
            </div>
            <div className="field">
              <label className="label">Select invoice date:</label>
              <DatePicker
                format={'DD/MM/YYYY'}
                onChange={(date) => {
                  setState({
                    ...state,
                    fecha_factura: `${date._d.getFullYear()}-${
                      date._d.getMonth() + 1
                    }-${date._d.getDate()}`,
                  })
                }}
              />
            </div>
            <Form.Item>
              <Space direction="horizontal" size="large">
                <Button type="primary" htmlType="submit" loading={loading}>
                  Get data
                </Button>
                <Button
                  type="dashed"
                  htmlType="button"
                  onClick={() => window.location.reload(false)}
                >
                  Reset
                </Button>
              </Space>
            </Form.Item>
          </div>
        )}
        {dataTransportCost && (
          <div style={{ 'margin-top': '10px' }}>
            <div>
              Total value of lines with matching order:{' '}
              <b>{formatCurrency(dataTransportCost.total_cost_with_referer)}</b>
            </div>
            <div>
              Total value of lines with no matching order:{' '}
              <b>
                {formatCurrency(dataTransportCost.total_cost_without_referer)}
              </b>
            </div>
            <div>
              Total invoice value, VAT excluded (calculated):{' '}
              <b>{formatCurrency(dataTransportCost.cost_calculate)}</b>
            </div>
            <div>
              <Form.Item>
                <Space direction="horizontal" size="large">
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Confirm data
                  </Button>
                  <Button
                    type="dashed"
                    htmlType="button"
                    onClick={() => window.location.reload(false)}
                  >
                    Reset
                  </Button>
                </Space>
              </Form.Item>
            </div>
          </div>
        )}
        {optionFilter ? (
          template === 'shipping' ? (
            <Form onFinish={filterDataInvoices}>
              <div className="field" style={{ marginTop: '30px' }}>
                <h2 className="title is-size-5">Filter by dates</h2>
                <RangePicker
                  format={'DD/MM/YYYY'}
                  onChange={(date) => {
                    setFilterData({
                      template: template,
                      fecha_desde: `${date[0]._d.getFullYear()}-${
                        date[0]._d.getMonth() + 1
                      }-${date[0]._d.getDate()}`,
                      fecha_hasta: `${date[1]._d.getFullYear()}-${
                        date[1]._d.getMonth() + 1
                      }-${date[1]._d.getDate()}`,
                    })
                  }}
                />
              </div>
              <Form.Item>
                <Space direction="horizontal" size="large">
                  <Button type="primary" htmlType="submit" loading={loading}>
                    Filter
                  </Button>
                  {totalFilter ? (
                    <>
                      <Button
                        type="dashed"
                        htmlType="button"
                        onClick={() => window.location.reload(false)}
                      >
                        Reset
                      </Button>
                      <label>Total amount: {totalFilter}</label>
                    </>
                  ) : null}
                </Space>
              </Form.Item>
            </Form>
          ) : null
        ) : null}
        <div style={{ 'margin-top': '10px' }}>
          <Table
            columns={columnsTable}
            dataSource={data}
            bordered
            sticky
            scroll={{
              x: true,
              y: true,
            }}
            size="small"
            pagination={false}
          />
        </div>
      </Form>
    </div>
  )
}
